import type { ApiPlayable } from "@hive/sdk";
import { getI18n } from "../i18n";

export function playableName(playable: ApiPlayable): string {
	return playable.title || getI18n().t("playable.untitled");
}

export function playableTagline(playable: ApiPlayable): string {
	return playable.summary || getI18n().t("playable.defaultTagline");
}

/**
 * 二创权限: hive settles every remix scope for the viewer in `viewerCanRemix`,
 * so the entry we draw matches what the remix handoff will actually allow. The
 * fallback covers responses that carry no viewer state — anonymous reads, the
 * viewer-blind CMS/tag delivery paths, a stale feed cache — and stays
 * optimistic there, since hive still refuses the handoff itself.
 */
export function canRemixPlayable(playable: ApiPlayable, viewerHandle: string | null): boolean {
	const settled = playable.viewerCanRemix;
	if (typeof settled === "boolean") return settled;
	const isAuthor = viewerHandle !== null && playable.author?.handle === viewerHandle;
	return isAuthor || playable.remixScope !== "ONLY_ME";
}

/**
 * 二创置灰: why the remix entry is greyed out, as the copy to show, or null when
 * it is open. A game remix can't start from (hive's `remixSupported: false` —
 * poki / manually entered games) comes first and blocks the author too. The
 * pinned SDK types and pre-deploy cached responses don't carry the field yet,
 * so only an explicit false counts.
 */
export function remixBlockedKey(playable: ApiPlayable, viewerHandle: string | null) {
	const { remixSupported } = playable as ApiPlayable & { remixSupported?: boolean | null };
	if (remixSupported === false) return "createGame.remixUnsupported" as const;
	return canRemixPlayable(playable, viewerHandle) ? null : ("createGame.remixNotAllowed" as const);
}

export function authorLabel(playable: ApiPlayable): string {
	if (!playable.author) return getI18n().t("playable.anonymousAuthor");
	return playable.author.displayName || `@${playable.author.handle}`;
}

/**
 * 预览视频: hive serves `previewVideoUrl` on the playable, but the published SDK
 * types still predate the field — read it structurally until the dependency
 * catches up. Only operators can set one, so an absent video is the normal case.
 */
export function playablePreviewVideo(playable: ApiPlayable): string | null {
	const { previewVideoUrl } = playable as ApiPlayable & { previewVideoUrl?: string | null };
	return previewVideoUrl || null;
}

const COVER_GRADIENTS = [
	"linear-gradient(135deg, #ff7a59, #ff4d8d 55%, #7c5cff)",
	"linear-gradient(135deg, #ff5c8a, #b14bff)",
	"linear-gradient(135deg, #35e0c8, #2f80ed)",
	"linear-gradient(135deg, #7c5cff, #ff5c8a)",
	"linear-gradient(135deg, #ffcf6b, #ff5c8a 60%, #ff7a59)",
	"linear-gradient(135deg, #35e0c8, #6ee7a8)",
	"linear-gradient(135deg, #4b3b8f, #1d1730)",
];

/** Deterministic fallback cover gradient for a playable with no cover image. */
export function coverGradient(playable: ApiPlayable): string {
	let total = 0;
	for (const ch of playable.id) total += ch.charCodeAt(0);
	return COVER_GRADIENTS[total % COVER_GRADIENTS.length];
}

/**
 * 作者视角: hive only fills the owner-only state (草稿/已下架, 未发布修改, 非公开
 * 可见范围, 匿名发布) in for the author, so every surface that renders it has to
 * agree on who the author is. `authorId` is the side to compare — a 匿名作品
 * hides the author object from everyone else, and an absent handle must not
 * read as a match.
 */
export function isPlayableAuthor(playable: ApiPlayable, viewerId: string | null): boolean {
	return viewerId !== null && playable.authorId !== null && playable.authorId === viewerId;
}

/**
 * 限时作品: past its `expiresAt` deadline. Hive keeps expired work out of the
 * feed and its listings, so this only fires where a reference outlives that
 * filter — a shared link opened after the deadline, or a list held open long
 * enough for one of its items to lapse.
 */
export function playableExpired(playable: ApiPlayable, now: number = Date.now()): boolean {
	if (!playable.expiresAt) return false;
	const deadline = new Date(playable.expiresAt).getTime();
	return !Number.isNaN(deadline) && deadline <= now;
}

/** 限时作品 chip: the countdown, or the past-tense label once it has lapsed. Null when there is no deadline. */
export function expiryBadge(
	playable: ApiPlayable,
	now: number = Date.now(),
): { label: string; expired: boolean } | null {
	if (!playable.expiresAt) return null;
	const deadline = new Date(playable.expiresAt).getTime();
	if (Number.isNaN(deadline)) return null;
	if (deadline <= now) return { label: getI18n().t("playable.expired"), expired: true };

	const minutes = Math.floor((deadline - now) / 60_000);
	if (minutes < 60) {
		return {
			label: getI18n().t("playable.expiresInMinutes", { count: Math.max(1, minutes) }),
			expired: false,
		};
	}
	const hours = Math.floor(minutes / 60);
	if (hours < 24) return { label: getI18n().t("playable.expiresInHours", { count: hours }), expired: false };
	return { label: getI18n().t("playable.expiresInDays", { count: Math.floor(hours / 24) }), expired: false };
}

/**
 * 可见范围 chip for the author. PUBLIC is the default and gets no chip; every
 * other scope is worth stating on the author's own surfaces, because hive hides
 * those posts from the audience they exclude without telling anyone.
 */
const VISIBILITY_LABEL_KEYS = {
	FOLLOWERS: "playable.visibilityFollowers",
	FRIENDS: "playable.visibilityFriends",
	UNLISTED: "playable.visibilityUnlisted",
	PRIVATE: "playable.visibilityPrivate",
	DIRECTED: "playable.visibilityDirected",
} as const;

export function visibilityLabelKey(visibility: ApiPlayable["visibility"]) {
	return VISIBILITY_LABEL_KEYS[visibility as keyof typeof VISIBILITY_LABEL_KEYS] ?? null;
}
